import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { OutputCard, CompliancePanel } from "@/components/OutputCard";
import { Switch } from "@/components/ui/switch";
import { FileText, MapPin, Megaphone, Sparkles, Wand2, RefreshCw, Mail, Video, Newspaper, Mic, Square, MapPinned, Gauge, TrendingUp } from "lucide-react";

const coachColor = (s) => (s >= 75 ? "#34D399" : s >= 50 ? "#FBBF24" : "#F87171");

function CoachPanel({ data, loading, onApply, applying }) {
  if (loading) return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 flex items-center gap-2 text-sm text-slate-500" data-testid="coach-loading">
      <RefreshCw className="h-4 w-4 animate-spin" /> Coach is scoring your description…
    </div>
  );
  if (!data) return null;
  const c = coachColor(data.score);
  const verdict = data.score >= 75 ? "Strong listing — just minor polish available."
    : data.score >= 50 ? "Decent — a few tweaks will lift engagement."
    : "Needs work — apply the coach's rewrite for a stronger hook.";
  return (
    <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white rounded-lg p-5" data-testid="coach-panel">
      <div className="flex items-center gap-4">
        <div className="h-16 w-16 rounded-full shrink-0 flex items-center justify-center" style={{ background: `conic-gradient(${c} ${data.score * 3.6}deg, #334155 0deg)` }}>
          <div className="h-12 w-12 rounded-full bg-slate-900 flex items-center justify-center text-lg font-bold" style={{ color: c }} data-testid="coach-score">{data.score}</div>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs uppercase tracking-wide text-slate-400 flex items-center gap-1.5"><Gauge className="h-3.5 w-3.5" /> Coach score · grade {data.grade}</p>
          <p className="text-sm text-slate-200 mt-1">{verdict}</p>
        </div>
      </div>
      {data.improvements?.length > 0 && (
        <ul className="mt-4 space-y-1.5" data-testid="coach-tips">
          {data.improvements.slice(0, 3).map((t, i) => (
            <li key={i} className="text-sm text-slate-300 flex gap-2"><TrendingUp className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />{t.tip}</li>
          ))}
        </ul>
      )}
      {data.rewrite && (
        <Button onClick={onApply} disabled={applying} data-testid="coach-apply-btn" className="mt-4 bg-white text-slate-900 hover:bg-slate-100 font-semibold">
          {applying ? <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Improving…</> : <><Wand2 className="h-4 w-4 mr-2" /> Apply improved rewrite & re-score</>}
        </Button>
      )}
    </div>
  );
}

const TONES = ["luxury", "cozy", "modern", "family-friendly", "investor-focused"];
const PLATFORMS = ["Instagram", "Facebook", "LinkedIn"];
const TYPES = ["Single-family home", "Condo", "Townhouse", "Apartment", "Penthouse", "Villa", "Land", "Multi-family"];

const EMPTY = {
  property_type: "Single-family home", beds: "", baths: "", sqft: "",
  features: "", location: "", nearby: "", tone: "modern", target_buyer: "", platform: "Instagram",
};

export default function GenerateView({ onUsed }) {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [rewriting, setRewriting] = useState(false);
  const [rewriteTone, setRewriteTone] = useState("luxury");
  const [address, setAddress] = useState("");
  const [autofilling, setAutofilling] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [coach, setCoach] = useState(true);
  const [coachData, setCoachData] = useState(null);
  const [coaching, setCoaching] = useState(false);
  const [applyingCoach, setApplyingCoach] = useState(false);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);

  const scoreDescription = async (text) => {
    if (!text) return;
    setCoaching(true); setCoachData(null);
    try {
      const { data } = await api.post("/intel/predict", { copy: text, target_buyer: form.target_buyer });
      setCoachData(data);
    } catch { /* coach is a bonus — fail silently */ }
    finally { setCoaching(false); }
  };

  const applyCoachRewrite = async () => {
    if (!coachData?.rewrite) return;
    setApplyingCoach(true);
    const newText = coachData.rewrite;
    setResult((r) => ({ ...r, outputs: { ...r.outputs, description: newText } }));
    toast.success("Applied the coach's rewrite.");
    try {
      const { data } = await api.post("/intel/predict", { copy: newText, target_buyer: form.target_buyer });
      setCoachData(data);
    } catch { /* ignore */ }
    finally { setApplyingCoach(false); }
  };

  const applyDetails = (d) => {
    setForm((f) => {
      const next = { ...f };
      Object.keys(d).forEach((k) => { if (d[k]) next[k] = d[k]; });
      return next;
    });
  };

  const autofillAddress = async () => {
    if (!address.trim()) { toast.error("Enter a property address."); return; }
    setAutofilling(true);
    try {
      const { data } = await api.post("/autofill", { address });
      applyDetails(data.details);
      toast.success(data.estimated ? "Auto-filled (AI estimate — please verify)." : "Auto-filled from address.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setAutofilling(false);
    }
  };

  const startDictation = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        const reader = new FileReader();
        reader.onloadend = async () => {
          setTranscribing(true);
          try {
            const { data: t } = await api.post("/voice/transcribe", { audio_base64: reader.result, mime: blob.type });
            if (!t.text) { toast.error("Didn't catch that — try again."); return; }
            toast.success("Transcribed — filling the form…");
            const { data } = await api.post("/autofill", { text: t.text });
            applyDetails(data.details);
            if (!data.details.features) setForm((f) => ({ ...f, features: t.text }));
          } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
          } finally {
            setTranscribing(false);
          }
        };
        reader.readAsDataURL(blob);
      };
      rec.start();
      recorderRef.current = rec;
      setRecording(true);
    } catch {
      toast.error("Microphone access denied or unavailable.");
    }
  };

  const stopDictation = () => {
    recorderRef.current?.stop();
    setRecording(false);
  };

  useEffect(() => {
    const prefill = sessionStorage.getItem("penhaus_prefill");
    if (prefill) {
      try {
        const p = JSON.parse(prefill);
        setForm((f) => ({ ...f, ...p }));
        toast.info("Features from your photo were added.");
      } catch {}
      sessionStorage.removeItem("penhaus_prefill");
    }
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setSel = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const generate = async () => {
    if (!form.features || !form.location) {
      toast.error("Add at least key features and a location.");
      return;
    }
    setLoading(true);
    setResult(null);
    setCoachData(null);
    try {
      const { data } = await api.post("/generate", form);
      setResult(data);
      onUsed?.();
      toast.success("Listing kit generated!");
      if (coach) scoreDescription(data.outputs.description);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const rewrite = async () => {
    if (!result?.outputs?.description) return;
    setRewriting(true);
    try {
      const { data } = await api.post("/rewrite", {
        text: result.outputs.description,
        new_tone: rewriteTone,
        new_target_buyer: form.target_buyer || "buyers",
      });
      setResult((r) => ({ ...r, outputs: { ...r.outputs, description: data.text } }));
      toast.success(`Re-spun in a ${rewriteTone} tone`);
      if (coach) scoreDescription(data.text);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setRewriting(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-5 gap-8">
      {/* Form */}
      <div className="lg:col-span-2">
        <div className="bg-white border border-slate-200 rounded-lg p-6 sticky top-24">
          <h2 className="font-semibold text-lg flex items-center gap-2"><Wand2 className="h-5 w-5" /> Property details</h2>
          <p className="text-sm text-slate-500 mt-1">Fill the basics — we'll write the rest.</p>

          <div className="mt-4 rounded-lg bg-slate-50 border border-slate-200 p-3 space-y-3">
            <div className="flex gap-2">
              <Input data-testid="autofill-address-input" value={address} onChange={(e) => setAddress(e.target.value)}
                placeholder="Paste an address to auto-fill…" className="h-9 bg-white" />
              <Button onClick={autofillAddress} disabled={autofilling} variant="outline" size="sm" className="shrink-0 h-9" data-testid="autofill-btn">
                {autofilling ? <RefreshCw className="h-4 w-4 animate-spin" /> : <MapPinned className="h-4 w-4" />}
                <span className="ml-1.5 hidden sm:inline">Auto-fill</span>
              </Button>
            </div>
            <button type="button" onClick={recording ? stopDictation : startDictation} disabled={transcribing} data-testid="dictate-btn"
              className={`w-full flex items-center justify-center gap-2 h-9 rounded-md text-sm font-medium transition-colors ${recording ? "bg-red-600 text-white hover:bg-red-700" : "bg-slate-900 text-white hover:bg-slate-800"} disabled:opacity-60`}>
              {transcribing ? <><RefreshCw className="h-4 w-4 animate-spin" /> Transcribing…</>
                : recording ? <><Square className="h-4 w-4" /> Stop & transcribe</>
                : <><Mic className="h-4 w-4" /> Dictate property (talk it through)</>}
            </button>
            <p className="text-[11px] text-slate-400 leading-tight">Tip: just talk — "three beds, renovated kitchen, killer backyard." Address auto-fill is an AI estimate; verify details.</p>
          </div>

          <div className="space-y-4 mt-5">
            <div>
              <Label>Property type</Label>
              <Select value={form.property_type} onValueChange={setSel("property_type")}>
                <SelectTrigger className="mt-1.5" data-testid="property-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Beds</Label><Input data-testid="property-beds-input" value={form.beds} onChange={set("beds")} placeholder="3" className="mt-1.5" /></div>
              <div><Label>Baths</Label><Input data-testid="property-baths-input" value={form.baths} onChange={set("baths")} placeholder="2" className="mt-1.5" /></div>
              <div><Label>Sq ft</Label><Input data-testid="property-sqft-input" value={form.sqft} onChange={set("sqft")} placeholder="1850" className="mt-1.5" /></div>
            </div>
            <div>
              <Label>Key features</Label>
              <Textarea data-testid="property-features-input" value={form.features} onChange={set("features")} className="mt-1.5" rows={3}
                placeholder="Chef's kitchen, quartz counters, vaulted ceilings, heated pool…" />
            </div>
            <div>
              <Label>Neighborhood / location</Label>
              <Input data-testid="property-location-input" value={form.location} onChange={set("location")} className="mt-1.5" placeholder="Highland Park, Austin TX" />
            </div>
            <div>
              <Label>Nearby (schools, parks, dining, transit)</Label>
              <Input data-testid="property-nearby-input" value={form.nearby} onChange={set("nearby")} className="mt-1.5" placeholder="Top-rated schools, lakefront trails, cafes" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tone</Label>
                <Select value={form.tone} onValueChange={setSel("tone")}>
                  <SelectTrigger className="mt-1.5 capitalize" data-testid="property-tone-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{TONES.map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Social platform</Label>
                <Select value={form.platform} onValueChange={setSel("platform")}>
                  <SelectTrigger className="mt-1.5" data-testid="property-platform-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{PLATFORMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Target buyer</Label>
              <Input data-testid="property-buyer-input" value={form.target_buyer} onChange={set("target_buyer")} className="mt-1.5" placeholder="First-time buyers, downsizers, investors…" />
            </div>
            <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2.5">
              <span className="flex items-center gap-2 text-sm text-slate-700"><Gauge className="h-4 w-4 text-slate-500" /> Coach mode — auto-score & suggest fixes</span>
              <Switch checked={coach} onCheckedChange={setCoach} data-testid="coach-toggle" />
            </div>
            <Button onClick={generate} disabled={loading} data-testid="generate-copy-button"
              className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white font-semibold">
              {loading ? <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Building your kit…</> : <><Sparkles className="h-4 w-4 mr-2" /> Generate Full Marketing Kit</>}
            </Button>
            <p className="text-xs text-slate-400 mt-2 text-center">Description · Neighborhood · Social · Headlines · Email · Video · Flyer</p>
          </div>
        </div>
      </div>

      {/* Output */}
      <div className="lg:col-span-3 space-y-5">
        {!result && !loading && (
          <div className="bg-white border border-dashed border-slate-300 rounded-lg p-12 text-center" data-testid="generate-empty-state">
            <FileText className="h-10 w-10 text-slate-300 mx-auto" />
            <p className="text-slate-500 mt-4">Your polished listing, neighborhood copy, social captions, and headlines will appear here.</p>
          </div>
        )}
        {loading && (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => <div key={i} className="h-40 bg-white border border-slate-200 rounded-lg animate-pulse" />)}
          </div>
        )}
        {result && (
          <>
            <CompliancePanel compliance={result.outputs.compliance} />
            <OutputCard title="Listing Description" icon={FileText} testid="output-description" copyText={result.outputs.description}>
              {result.outputs.description}
            </OutputCard>
            {coach && <CoachPanel data={coachData} loading={coaching} onApply={applyCoachRewrite} applying={applyingCoach} />}
            <div className="bg-white border border-slate-200 rounded-lg p-4 flex flex-wrap items-center gap-3">
              <span className="text-sm font-medium text-slate-600">Quick re-spin:</span>
              <Select value={rewriteTone} onValueChange={setRewriteTone}>
                <SelectTrigger className="w-40 h-9 capitalize" data-testid="rewrite-tone-select"><SelectValue /></SelectTrigger>
                <SelectContent>{TONES.map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
              </Select>
              <Button onClick={rewrite} disabled={rewriting} variant="outline" size="sm" data-testid="rewrite-btn">
                {rewriting ? <RefreshCw className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />} Rewrite
              </Button>
            </div>
            <OutputCard title="Neighborhood Highlights" icon={MapPin} testid="output-neighborhood" copyText={result.outputs.neighborhood}>
              {result.outputs.neighborhood}
            </OutputCard>
            <OutputCard title="Social Captions" icon={Megaphone} testid="output-social" copyText={result.outputs.social}>
              {result.outputs.social}
            </OutputCard>
            <OutputCard title="Headlines & Hooks" icon={Sparkles} testid="output-headlines" copyText={result.outputs.headlines}>
              {result.outputs.headlines}
            </OutputCard>
            <OutputCard title='"Just Listed" Email' icon={Mail} testid="output-email" copyText={result.outputs.email}>
              {result.outputs.email}
            </OutputCard>
            <OutputCard title="Video Walkthrough Script" icon={Video} testid="output-video" copyText={result.outputs.video}>
              {result.outputs.video}
            </OutputCard>
            {result.outputs.flyer && (
              <OutputCard title="Printable Flyer Copy" icon={Newspaper} testid="output-flyer" copyText={result.outputs.flyer} printable>
                {result.outputs.flyer}
              </OutputCard>
            )}
          </>
        )}
      </div>
    </div>
  );
}
